/**
 * Media Server Connection Hook
 * Shared utility for checking VPN/media server connectivity
 * Used by: OpsDashboard, AircraftTracker, VideoLibrary
 * 
 * The backend can always reach the media server via SSH.
 * This check is for the USER's browser connectivity to the media server
 * (i.e., whether they are on the VPN or in the office).
 */
import { useState, useEffect, useCallback } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL || "";
const MEDIA_SERVER_TEST_URL = "https://srv-ops.nsas.tech/api/streams"; // NSAS Video Server API

// Cache the connection status to avoid multiple checks
let connectionCache = {
  status: null,
  timestamp: 0,
  ttl: 30000, // 30 seconds cache
  pendingPromise: null
};

/**
 * Check if user's browser can reach the media server
 * This tests actual browser connectivity (VPN/local network access)
 * We test by attempting to fetch a known page from the media server
 */
const checkBrowserConnectivity = async () => {
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 8000); // 8 second timeout
    
    // Try to fetch the play.htm page - this is a known page on the media server
    // Using no-cors mode because CORS won't be configured for our domain
    // If the fetch completes without error, the server is reachable
    const response = await fetch(MEDIA_SERVER_TEST_URL, {
      method: 'HEAD',
      mode: 'no-cors', // no-cors mode to avoid CORS issues
      signal: controller.signal,
      cache: 'no-store' // Don't use cached results
    });
    
    clearTimeout(timeoutId);
    
    // If we get here without an error, the server is reachable
    // no-cors mode doesn't give us response status, but if it doesn't throw, it connected
    return { connected: true, message: "Media server reachable" };
    
  } catch (error) {
    if (error.name === 'AbortError') {
      return { connected: false, message: "Connection timed out - VPN required" };
    }
    // Network error - likely not on VPN
    console.log('Media server connectivity check failed:', error.message);
    return { connected: false, message: "Cannot reach media server - VPN required" };
  }
};

/**
 * Check media server connection status
 * First tries browser connectivity, falls back to backend check
 */
export const checkMediaServerConnection = async (forceRefresh = false) => {
  const now = Date.now();
  
  // Return cached result if still valid and not forcing refresh
  if (!forceRefresh && connectionCache.status !== null && 
      now - connectionCache.timestamp < connectionCache.ttl) {
    return connectionCache.status;
  }
  
  // If there's already a pending request, wait for it
  if (!forceRefresh && connectionCache.pendingPromise) {
    return connectionCache.pendingPromise;
  }
  
  // Make the check
  connectionCache.pendingPromise = (async () => {
    try {
      // First try browser connectivity (this is what matters for video playback)
      const browserCheck = await checkBrowserConnectivity();
      
      connectionCache.status = {
        connected: browserCheck.connected,
        message: browserCheck.message,
        server: "ops.nsas.tech",
        checkedAt: new Date().toISOString()
      };
      connectionCache.timestamp = Date.now();
      return connectionCache.status;
      
    } catch (error) {
      console.error('Media server connection check failed:', error);
      const errorStatus = {
        connected: false,
        message: `Check failed: ${error.message}`,
        server: 'ops.nsas.tech',
        checkedAt: new Date().toISOString()
      };
      connectionCache.status = errorStatus;
      connectionCache.timestamp = Date.now();
      return errorStatus;
    } finally {
      connectionCache.pendingPromise = null;
    }
  })();
  
  return connectionCache.pendingPromise;
};

/**
 * Clear the connection cache
 * Useful when user wants to retry
 */
export const clearConnectionCache = () => {
  connectionCache.status = null;
  connectionCache.timestamp = 0;
  connectionCache.pendingPromise = null;
};

/**
 * React hook for media server connection status
 * Provides reactive state updates and retry functionality
 */
export const useMediaServerConnection = (autoCheck = true) => {
  const [isConnected, setIsConnected] = useState(null); // null = checking, true/false = result
  const [isChecking, setIsChecking] = useState(false);
  const [error, setError] = useState(null);
  const [lastChecked, setLastChecked] = useState(null);

  const checkConnection = useCallback(async (force = false) => {
    setIsChecking(true);
    setError(null);
    
    if (force) {
      clearConnectionCache();
    }
    
    try {
      const result = await checkMediaServerConnection(force);
      setIsConnected(result.connected);
      setLastChecked(new Date());
      
      if (!result.connected) {
        setError(result.message);
      }
      
      return result;
    } catch (err) {
      setIsConnected(false);
      setError(err.message || 'Connection check failed');
      return { connected: false, message: err.message };
    } finally {
      setIsChecking(false);
    }
  }, []);

  // Auto-check on mount if enabled
  useEffect(() => {
    if (autoCheck) {
      checkConnection();
    }
  }, [autoCheck, checkConnection]);

  return {
    isConnected,
    isChecking,
    error,
    lastChecked,
    checkConnection,
    retry: () => checkConnection(true)
  };
};

export default useMediaServerConnection;
